import {
  Box,
  List as MuiList,
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Collapse,
} from "@mui/material";
import styled from "styled-components";
import {
  KeyboardArrowUpOutlined,
  KeyboardArrowDownOutlined,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useMemo, useState } from "react";
import { BiRadioCircle } from "react-icons/bi";
import { RxDividerVertical } from "react-icons/rx";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useNavigate, useLocation } from "react-router-dom";

import sidebarRoutes from "./sidebarRoutes";
import { colors } from "../../../pages/css components/Style";
import {
  setParentName,
  setSubParentName,
} from "../../../redux/slices/memberSlice";

const Wrapper = styled(Box)`
  flex: 1;
  max-height: 100%;
  overflow: auto;
`;

const List = styled(MuiList)`
  padding: 0;
`;

const ListItemButton = styled(MuiListItemButton)`
  height: 48px;
  margin: 4px 0;
  padding: 10px 16px;
  border-radius: 12px;
  transition: background 0.2s;
  &:hover {
    background-color: #f2e6f5;
  }
`;

const ListItemIcon = styled(MuiListItemIcon)`
  min-width: 0;
  margin-right: 16px;
`;

const Text = styled(MuiListItemText)`
  .MuiListItemText-primary {
    font-size: 15px;
    color: ${({ active }) => (active ? colors.purple : "#504d4dff")};
  }
`;

const Scrollbar = styled(PerfectScrollbar)`
  .ps__rail-y {
    opacity: 0 !important; /* Hide scrollbar when inactive */
    transition: opacity 0.2s ease;
  }

  .ps--active-y .ps__rail-y {
    opacity: 1 !important; /* Show only when scroll needed */
  }
`;

const getIconStyle = (isActive) => ({
  fontSize: 22,
  color: isActive ? colors.purple : "#504d4dff",
});

export default function Menu({ setSelectedMenu }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { dashboardPermissions, agent, parentName, subParentName } =
    useSelector((state) => state.memberDetails);

  const [folderOpen, setFolderOpen] = useState({});
  const [subFolderOpen, setSubFolderOpen] = useState({});

  useEffect(() => {
    const p = sessionStorage.getItem("parentName") || parentName;
    const s = sessionStorage.getItem("subParentName") || subParentName;
    setFolderOpen({ [p]: true });
    setSubFolderOpen({ [s]: true });
  }, [parentName, subParentName]);

  const permittedRoutes = useMemo(() => {
    return sidebarRoutes.reduce((acc, route) => {
      if (route.children) {
        const children = route.children.filter((child) =>
          dashboardPermissions?.some((p) => p.ID === child.ID)
        );
        if (children.length) acc.push({ ...route, children });
      } else {
        const permitted = dashboardPermissions?.some((p) => p.ID === route.ID);
        if (permitted && !(route.ID === "template-list" && !agent?.length))
          acc.push(route);
      }
      return acc;
    }, []);
  }, [dashboardPermissions, agent]);

  const navigateTo = (path, parent, sub = "") => {
    navigate(path);
    setSelectedMenu(path);
    dispatch(setParentName(parent));
    sessionStorage.setItem("parentName", parent);

    if (sub) {
      dispatch(setSubParentName(sub));
      sessionStorage.setItem("subParentName", sub);
    } else {
      dispatch(setSubParentName(""));
      sessionStorage.removeItem("subParentName");
    }
  };

  const isMatch = (path) =>
    path && location.pathname.startsWith("/" + path.split("/")[1]);

  const RenderItem = ({
    name,
    icon: Icon,
    path,
    parent,
    sub,
    isSub,
    hasChildren,
  }) => {
    const active = isMatch(path);
    const bg = active && !isSub ? "#ff6d1d12" : "#fff";

    return (
      <ListItemButton
        style={{ backgroundColor: bg }}
        onClick={() => navigateTo(path, parent, sub)}
      >
        {/* <ListItemIcon>
          <Icon style={getIconStyle(active)} />
        </ListItemIcon> */}
        <Text
          style={{ color: active && isSub ? "#8f1c8214" : "#504d4dff" }}
          primary={name}
          active={!isSub && active}
        />
      </ListItemButton>
    );
  };

  const RenderFolder = ({ name, icon: Icon, children, hasChildren }) => {
    const open = folderOpen[name];
    const active = parentName === name;

    return (
      <Fragment>
        <ListItemButton
          onClick={() => setFolderOpen({ ...folderOpen, [name]: !open })}
          style={{
            backgroundColor: active ? "#ff6d1d12" : "#fff",
          }}
        >
          {/* <ListItemIcon>
            <Icon style={getIconStyle(active)} />
          </ListItemIcon> */}
          <Text primary={name} active={active} />
          {open ? (
            <KeyboardArrowUpOutlined style={getIconStyle(active)} />
          ) : (
            <KeyboardArrowDownOutlined style={getIconStyle(active)} />
          )}
        </ListItemButton>

        <Collapse in={open}>
          <List>
            {children.map(({ name: childName, ID, path, icon, subChilds }) =>
              subChilds ? (
                <RenderSubFolder
                  key={ID}
                  parent={name}
                  name={childName}
                  children={subChilds}
                  icon={icon}
                  hasChildren={hasChildren}
                />
              ) : (
                <RenderItem
                  key={ID}
                  name={childName}
                  icon={BiRadioCircle}
                  path={path}
                  parent={name}
                  sub={childName}
                  hasChildren={hasChildren}
                />
              )
            )}
          </List>
        </Collapse>
      </Fragment>
    );
  };

  const RenderSubFolder = ({ parent, name, children, icon: Icon }) => {
    const open = subFolderOpen[name];
    const active = subParentName === name;

    return (
      <Fragment>
        <ListItemButton
          onClick={() => setSubFolderOpen({ ...subFolderOpen, [name]: !open })}
          style={{ backgroundColor: active ? "#ff6d1d12" : "#fff" }}
        >
          <ListItemIcon>
            <Icon style={getIconStyle(active)} />
          </ListItemIcon>
          <Text primary={name} active={active} />
          {open ? (
            <KeyboardArrowUpOutlined style={getIconStyle(active)} />
          ) : (
            <KeyboardArrowDownOutlined style={getIconStyle(active)} />
          )}
        </ListItemButton>

        <Collapse in={open} style={{ marginInlineStart: 10 }}>
          <List>
            {children.map(({ name: cName, ID, path }) => (
              <RenderItem
                key={ID}
                name={cName}
                path={path}
                icon={RxDividerVertical}
                parent={parent}
                sub={name}
                isSub={true}
              />
            ))}
          </List>
        </Collapse>
      </Fragment>
    );
  };

  return (
    <Wrapper>
      <Scrollbar options={{ suppressScrollX: true }}>
        <List>
          {permittedRoutes.map(({ name, icon, ID, path, children }) =>
            children ? (
              <RenderFolder
                key={ID}
                name={name}
                icon={icon}
                children={children}
                hasChildren={true}
              />
            ) : (
              <RenderItem
                key={ID}
                name={name}
                path={path}
                icon={icon}
                parent={name}
              />
            )
          )}
        </List>
      </Scrollbar>
    </Wrapper>
  );
}
